// Micro-system from the chat (decision D28, docs/delivery/microsystem-plan.md §2): the fixed HTTP contracts of
// `POST /v1/projects/from-brief` (WP3a) and of the publish endpoints (WP3b, added below in their own section).
import { z } from "zod";
import { projectSchema } from "./dto.ts";
import { repositoryProvisionSchema } from "./onboarding.ts";
import { assistantAttachmentIdsSchema } from "./assistant.ts";

// ---------- From brief (POST /v1/projects/from-brief) ----------

export const fromBriefRequestSchema = z.object({
  name: z.string().trim().min(1).max(80),
  brief: z.string().trim().min(1).max(20_000),
  /** Defaults to the organization's single active GitHub integration. */
  integration_id: z.uuid().optional(),
  /**
   * D36.4: composer attachments (screenshots, images, documents) that travel with the brief to the planner as untrusted
   * context: uploaded by the caller in Home (POST /v1/assistant/attachments without project_id), at most 5.
   */
  attachment_ids: assistantAttachmentIdsSchema.optional(),
});
export type FromBriefRequest = z.infer<typeof fromBriefRequestSchema>;

export const fromBriefRepositorySchema = z.object({
  full_name: z.string().regex(/^[A-Za-z0-9_.-]{1,100}\/[A-Za-z0-9_.-]{1,100}$/),
  html_url: z.string(),
  private: z.literal(true),
});
export type FromBriefRepository = z.infer<typeof fromBriefRepositorySchema>;

export const fromBriefResultSchema = z.object({
  project: projectSchema,
  repository: fromBriefRepositorySchema,
  provision: repositoryProvisionSchema,
  prd_version_id: z.uuid(),
  planning_run_id: z.uuid().nullable(),
});
export type FromBriefResult = z.infer<typeof fromBriefResultSchema>;

const GITHUB_NAME_MAX = 100;

/**
 * The repository name for a project name: kebab-case ASCII (accents folded, anything else a hyphen), at most 100
 * characters; `micro-system` when nothing usable is left. Always a valid GitHub repository name and npm package name.
 * Shared by from-brief (D28, the API) and the publish of a project without a repository (D33, the worker).
 */
export function repositoryNameFor(name: string): string {
  const slug = name
    .normalize("NFKD")
    .replace(/[̀-ͯ]/g, "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, GITHUB_NAME_MAX)
    .replace(/-+$/g, "");
  return slug === "" ? "micro-system" : slug;
}

/** `details.cause` of the 409 answers of from-brief. */
export const FROM_BRIEF_CONFLICTS = ["github_unavailable", "repository_exists"] as const;
export type FromBriefConflict = (typeof FROM_BRIEF_CONFLICTS)[number];


// =====================================================================================================================
// Publish (WP3b): POST/GET /v1/projects/:id/publish — push the workspace base branch to GitHub, create (once) a Coolify
// application from the private repository with a per-repository deploy key, deploy and record the release.
// =====================================================================================================================

/**
 * `creating_repository` (D33, migration 0022): a project without a GitHub repository mapping gets a private repository
 * created and mapped by the publish itself before the push; a mapped project goes from `queued` straight to `pushing`.
 */
export const PUBLISH_STATES = ["queued", "creating_repository", "pushing", "creating_app", "deploying", "succeeded", "failed"] as const;
export type PublishState = (typeof PUBLISH_STATES)[number];

/** States of a publish in flight: at most one per project (partial unique index `project_publishes_active_uq`, 0018). */
export const PUBLISH_ACTIVE_STATES = ["queued", "creating_repository", "pushing", "creating_app", "deploying"] as const satisfies readonly PublishState[];
export const PUBLISH_FINAL_STATES = ["succeeded", "failed"] as const satisfies readonly PublishState[];

/** Error vocabulary of a failed publish (kept in step with the 0018 CHECK, extended by 0022 for D33). */
export const PUBLISH_ERRORS = [
  "no_repository",
  "no_workspace",
  "github_unavailable",
  // D33 (0022): the publish creates the repository of a project that has none.
  "no_github_integration",
  "repository_exists",
  "repository_create_failed",
  "push_rejected",
  "push_failed",
  "coolify_not_configured",
  "coolify_unavailable",
  "deploy_key_failed",
  "app_create_failed",
  "deploy_trigger_failed",
  "deploy_failed",
  "deploy_timeout",
  "publish_failed",
] as const;
export type PublishError = (typeof PUBLISH_ERRORS)[number];

/**
 * Allowed moves of the publish state machine: forward through the pipeline, and `failed` from any state in flight.
 * Final states never move again.
 */
export const PUBLISH_TRANSITIONS: Readonly<Record<PublishState, readonly PublishState[]>> = {
  queued: ["creating_repository", "pushing", "failed"],
  creating_repository: ["pushing", "failed"],
  pushing: ["creating_app", "failed"],
  creating_app: ["deploying", "failed"],
  deploying: ["succeeded", "failed"],
  succeeded: [],
  failed: [],
};

export function publishTransitionAllowed(from: PublishState, to: PublishState): boolean {
  return PUBLISH_TRANSITIONS[from].includes(to);
}

/** The states from which `to` may be reached (the `from` set of a compare-and-set update). */
export function publishSourcesOf(to: PublishState): PublishState[] {
  return PUBLISH_STATES.filter((s) => PUBLISH_TRANSITIONS[s].includes(to));
}

export const projectPublishSchema = z.object({
  id: z.uuid(),
  project_id: z.uuid(),
  state: z.enum(PUBLISH_STATES),
  /** "owner/name" */
  repository: z.string().nullable(),
  commit_sha: z.string().nullable(),
  coolify_app_uuid: z.string().nullable(),
  deployment_uuid: z.string().nullable(),
  app_url: z.string().nullable(),
  error_code: z.enum(PUBLISH_ERRORS).nullable(),
  error: z.string().nullable(),
  created_at: z.iso.datetime(),
  updated_at: z.iso.datetime(),
  finished_at: z.iso.datetime().nullable(),
});
export type ProjectPublish = z.infer<typeof projectPublishSchema>;

/** POST body: empty object (the target comes from trusted configuration, never from the request). */
export const publishRequestSchema = z.object({}).strict();
export type PublishRequest = z.infer<typeof publishRequestSchema>;

export const publishResponseSchema = z.object({ publish: projectPublishSchema });
export const publishStatusResponseSchema = z.object({ publish: projectPublishSchema.nullable() });

/**
 * `details.reason` of the 409 `gate_pending` refusals of POST /v1/projects/:id/publish (D33). A project without a
 * repository mapping is no longer refused (the publish creates its repository) unless the organization has no usable
 * GitHub connection to create it with.
 */
export const PUBLISH_REFUSALS = ["no_workspace", "no_github_integration", "github_integration_ambiguous", "github_not_allowlisted"] as const;
export type PublishRefusalReason = (typeof PUBLISH_REFUSALS)[number];

// =====================================================================================================================
// End of the publish section.
// =====================================================================================================================
